import {
  E164_PHONE_PATTERN,
  inboundWebhookUrlProblem,
  TWILIO_ACCOUNT_SID_PATTERN,
  TWILIO_INBOUND_WEBHOOK_URL_MAX_LENGTH,
} from "@erp/shared-types";
import { oc } from "@orpc/contract";
import { z } from "zod";

export const TwilioPhoneNumberSchema = z.object({
  id: z.uuid(),
  phoneNumber: z.string(),
  createdAt: z.coerce.date(),
});

/**
 * Organization Twilio account. The auth token is write-only: responses say
 * whether one is stored and never include the token itself.
 */
export const TwilioAccountViewSchema = z.object({
  id: z.uuid(),
  accountSid: z.string(),
  authTokenSet: z.literal(true),
  /** Exact URL Twilio posts inbound SMS to, or null when inbound texts are off. */
  inboundWebhookUrl: z.string().nullable(),
  phoneNumbers: z.array(TwilioPhoneNumberSchema),
  updatedAt: z.coerce.date(),
});

export const GetTwilioAccountInputSchema = z.object({
  organizationId: z.uuid(),
});

export const SaveTwilioAccountInputSchema = z.object({
  organizationId: z.uuid(),
  accountSid: z
    .string()
    .regex(
      TWILIO_ACCOUNT_SID_PATTERN,
      "Account SID must start with AC followed by 32 hex characters",
    ),
  /** Omit or leave blank on update to keep the stored token. Required when creating. */
  authToken: z.string().max(128).optional(),
  /**
   * Exact public URL configured on the Twilio number. Blank clears it.
   * Omit to leave the stored URL unchanged.
   */
  inboundWebhookUrl: z
    .string()
    .max(TWILIO_INBOUND_WEBHOOK_URL_MAX_LENGTH)
    .refine((value) => inboundWebhookUrlProblem(value) === null, {
      message: "Inbound webhook URL must be an http or https URL",
    })
    .optional(),
});

export const AddTwilioPhoneNumberInputSchema = z.object({
  organizationId: z.uuid(),
  phoneNumber: z
    .string()
    .regex(E164_PHONE_PATTERN, "Phone number must be E.164, like +15551234567"),
});

export const TwilioPhoneNumberIdInputSchema = z.object({
  organizationId: z.uuid(),
  phoneNumberId: z.uuid(),
});

export const TwilioMutationResultSchema = z.object({
  success: z.boolean(),
});

export const twilioContracts = {
  getAccount: oc
    .input(GetTwilioAccountInputSchema)
    .output(TwilioAccountViewSchema.nullable())
    .route({
      summary: "Get the Twilio SMS account",
      description:
        "Returns the organization's Twilio account SID, inbound SMS webhook URL and configured phone numbers, or null if none is set up. The auth token is never returned.",
      method: "GET",
    }),

  saveAccount: oc
    .input(SaveTwilioAccountInputSchema)
    .output(TwilioAccountViewSchema)
    .route({
      summary: "Save the Twilio SMS account",
      description:
        "Creates or updates the organization's Twilio account SID, auth token (blank keeps the stored one) and inbound SMS webhook URL. Stored locally only; no call to Twilio.",
      method: "POST",
    }),

  addPhoneNumber: oc
    .input(AddTwilioPhoneNumberInputSchema)
    .output(TwilioPhoneNumberSchema)
    .route({
      summary: "Add a Twilio phone number",
      description:
        "Records an E.164 phone number under the organization's saved Twilio account for agent SMS. Does not purchase or provision the number with Twilio.",
      method: "POST",
    }),

  deletePhoneNumber: oc
    .input(TwilioPhoneNumberIdInputSchema)
    .output(TwilioMutationResultSchema)
    .route({
      summary: "Remove a Twilio phone number",
      description:
        "Removes a phone number from the organization's Twilio configuration. Does not release it in Twilio. Returns whether it was found.",
      method: "DELETE",
    }),

  deleteAccount: oc
    .input(GetTwilioAccountInputSchema)
    .output(TwilioMutationResultSchema)
    .route({
      summary: "Delete the Twilio SMS account",
      description:
        "Removes the organization's stored Twilio account and all its phone numbers. Returns whether an account existed.",
      method: "DELETE",
    }),
};

export type TwilioAccountView = z.infer<typeof TwilioAccountViewSchema>;
export type TwilioPhoneNumberView = z.infer<typeof TwilioPhoneNumberSchema>;
export type GetTwilioAccountInput = z.infer<typeof GetTwilioAccountInputSchema>;
export type SaveTwilioAccountInput = z.infer<
  typeof SaveTwilioAccountInputSchema
>;
export type AddTwilioPhoneNumberInput = z.infer<
  typeof AddTwilioPhoneNumberInputSchema
>;
export type TwilioPhoneNumberIdInput = z.infer<
  typeof TwilioPhoneNumberIdInputSchema
>;
